import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import mockTripData from "./mockTripData";
import { LocationIcons } from "./LocationIcons";
import buildOSRMUrl from "./OSRM";

// Helper to convert [lng, lat] to [lat, lng]
function toLatLng(coord) {
  return coord;
}

// Helper to convert [lat, lng] to [lng, lat]
function toLngLat(coord) {
  return [coord[1], coord[0]];
}

// Component to handle map clicks and add markers
function ClickPlotter({ onAdd }) {
  useMapEvents({
    click(e) {
      onAdd([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

// Pan map to center when trip changes
function PanToCenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position);
    }
  }, [position, map]);
  return null;
}

const TripMapOSRM = ({
  trip = mockTripData[0],
  plots,
  setPlots,
  customPlotter,
  handleUpdateDistanceInCustomTrip,
  setCustomPlotter,
  setIntermediateCoordinatesForSelectedRoute,
}) => {
  const [route, setRoute] = useState([]);
  const [loading, setLoading] = useState(false);

  // Prepare coordinates: start, places, end
  const coords = [
    toLatLng(trip.startLocation.coordinates),
    ...trip.places.map((p) => toLatLng(p.location.coordinates)),
    toLatLng(trip.endLocation.coordinates),
  ];
  const center = toLatLng(trip.startLocation.coordinates);

  const handleAddPlot = (latlng) => {
    console.log("Adding plot at:", latlng);
    setPlots((prev) => [...prev, latlng]);
  };

  useEffect(() => {
    async function fetchRoute() {
      setLoading(true);
      try {
        const url = buildOSRMUrl(coords);
        const res = await fetch(url);
        const data = await res.json();
        if (
          data.routes &&
          data.routes.length > 0 &&
          data.routes[0].geometry &&
          data.routes[0].geometry.coordinates
        ) {
          handleUpdateDistanceInCustomTrip(data.routes[0].distance || 0);
          setIntermediateCoordinatesForSelectedRoute?.(
            data.routes[0].geometry.coordinates
          ); // set the intermediate coordinates for the selected route for using it in s3 upload
          // OSRM returns [lng,lat], convert to [lat,lng] for leaflet
          const osrmRoute = data.routes[0].geometry.coordinates.map(
            ([lng, lat]) => [lat, lng]
          );
          setRoute(osrmRoute);
        } else {
          setRoute([]);
        }
      } catch (e) {
        setRoute([]);
      }
      setLoading(false);
    }
    fetchRoute();
    // eslint-disable-next-line
  }, [trip]);

  return (
    <MapContainer
      center={center}
      zoom={13}
      style={{ height: "400px", width: "100%" }}
    >
      <PanToCenter position={center} />
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />

      {!customPlotter ? (
        <>
          {/* OSRM Route */}
          {route.length > 0 && <Polyline positions={route} color="red" />}
          {/* Fallback: show straight line if OSRM fails */}
          {!loading && route.length === 0 && (
            <Polyline positions={coords} color="gray" dashArray="5,5" />
          )}
          <Marker
            position={toLatLng(trip.startLocation.coordinates)}
            icon={LocationIcons.startIcon}
          >
            <Popup>Start Location</Popup>
          </Marker>
          {trip.places.map((place) => (
            <Marker
              key={place.placeId}
              position={toLatLng(place.location.coordinates)}
              icon={LocationIcons.placeIcon}
            >
              <Popup>
                Place: {place.placeId}
                <br />
                Time: {place.time}
              </Popup>
            </Marker>
          ))}
          <Marker
            position={toLatLng(trip.endLocation.coordinates)}
            icon={LocationIcons.endIcon}
          >
            <Popup>End Location</Popup>
          </Marker>
        </>
      ) : (
        <>
          <Polyline positions={plots} color="blue" />
          {plots.map((latlng, idx) => (
            <Marker key={`plot-${idx}`} position={latlng}>
              <Popup>Custom Plot #{idx + 1}</Popup>
            </Marker>
          ))}
          <>{customPlotter && <ClickPlotter onAdd={handleAddPlot} />}</>
        </>
      )}
    </MapContainer>
  );
};

export default TripMapOSRM;
